/**
 * WebCodecs Video Exporter
 *
 * Exports timeline to WebM (VP9 + Opus) with burned-in subtitles
 * Supports multiple aspect ratios: 16:9, 1:1, 9:16
 */

import { Clip, Track, SubtitleStyle } from './types';
import { FrameRenderer } from './frame-renderer';
import { AudioMixer } from './audio-mixer';
import { Muxer, ArrayBufferTarget } from 'mp4-muxer';

// Aspect ratio configurations
export const ASPECT_RATIOS = {
  '16:9': { width: 1920, height: 1080 },
  '1:1': { width: 1080, height: 1080 },
  '9:16': { width: 1080, height: 1920 },
} as const;

export type AspectRatio = keyof typeof ASPECT_RATIOS;

export interface ExportConfig {
  aspectRatio: AspectRatio;
  fps: number;
  videoBitrate: number;
  audioBitrate: number;
  audioSampleRate: number;
  format?: string;
  burnSubtitles?: boolean;
  scaleMode?: 'fit' | 'fill';
  strategy?: 'prerender' | 'concat' | 'webcodecs' | 'smart-hybrid';
  userId?: string;
  projectId?: string;
}

export interface ExportProgress {
  phase: 'preparing' | 'encoding' | 'muxing' | 'complete' | 'error';
  progress: number; // 0-100
  message: string;
  currentFrame?: number;
  totalFrames?: number;
  details?: any; // Pour passer des infos détaillées (ex: liste des segments)
  chunks?: any[]; // Array of ChunkStatus for granular progress
}

export type ProgressCallback = (progress: ExportProgress) => void;

export class WebCodecsExporter {
  private config: ExportConfig;
  private progressCallback: ProgressCallback;
  private aborted = false;
  private videoEncoder?: VideoEncoder;
  // FIX: Cannot find name 'AudioEncoder'. Use `any` as a workaround for missing WebCodecs types.
  private audioEncoder?: any;
  private muxer?: Muxer<ArrayBufferTarget>;
  private frameRenderer?: FrameRenderer;
  private audioMixer?: AudioMixer;

  constructor(config: ExportConfig, progressCallback: ProgressCallback) {
    this.config = config;
    this.progressCallback = progressCallback;
  }

  /**
   * Main export function
   */
  async export(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    try {
      this.aborted = false;

      // Phase 1: Preparation
      this.updateProgress({
        phase: 'preparing',
        progress: 0,
        message: 'Préparation de l\'export...'
      });

      // Filter visible and audible clips
      const visibleClips = this.filterVisibleClips(clips, tracks);
      const audioClips = this.filterAudioClips(clips, tracks);

      // Calculate timeline duration
      const duration = this.calculateDuration(clips);
      const totalFrames = Math.ceil(duration * this.config.fps);

      this.updateProgress({
        phase: 'preparing',
        progress: 10,
        message: `${visibleClips.length} clips vidéo, ${audioClips.length} clips audio`,
        totalFrames
      });

      // Phase 2: Initialize encoders
      await this.initializeEncoders();

      this.updateProgress({
        phase: 'preparing',
        progress: 20,
        message: 'Encodeurs initialisés'
      });

      // Phase 3: Encode video and audio
      await this.encodeVideoAndAudio(
        visibleClips,
        audioClips,
        duration,
        totalFrames,
        // FIX: Pass 'tracks' to encodeVideoAndAudio to be used by the frame renderer.
        tracks,
        activeSubtitleLanguage,
        subtitleStyle
      );

      // Phase 4: Finalize
      this.updateProgress({
        phase: 'muxing',
        progress: 95,
        message: 'Finalisation du fichier MP4...'
      });

      const mp4Blob = await this.finalize();

      this.updateProgress({
        phase: 'complete',
        progress: 100,
        message: 'Export terminé !'
      });

      return mp4Blob;

    } catch (error) {
      // Check if it's a memory error
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      const isMemoryError = errorMessage.toLowerCase().includes('memory') ||
        errorMessage.toLowerCase().includes('out of') ||
        (error as any).name === 'QuotaExceededError';

      if (isMemoryError) {
        this.updateProgress({
          phase: 'error',
          progress: 0,
          message: `Erreur mémoire: Vidéo source trop volumineuse. Essayez de réduire la durée ou la qualité de la vidéo source.`
        });
      } else {
        this.updateProgress({
          phase: 'error',
          progress: 0,
          message: `Erreur: ${errorMessage}`
        });
      }
      throw error;
    } finally {
      this.cleanup();
    }
  }

  /**
   * Abort the export
   */
  abort() {
    this.aborted = true;
    this.cleanup();
  }

  /**
   * Filter visible clips based on track visibility
   */
  private filterVisibleClips(clips: Clip[], tracks: Track[]): Clip[] {
    return clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      return track?.isVisible &&
        (clip.type === 'video' || clip.type === 'image' || clip.type === 'text' || clip.type === 'zoom' || clip.type === 'synthe' || clip.type === 'panel');
    });
  }

  /**
   * Filter audio clips based on track mute status
   */
  private filterAudioClips(clips: Clip[], tracks: Track[]): Clip[] {
    return clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      return !track?.isMuted &&
        (clip.type === 'video' || clip.type === 'audio') &&
        clip.url;
    });
  }

  /**
   * Calculate total timeline duration
   */
  private calculateDuration(clips: Clip[]): number {
    if (clips.length === 0) return 0;
    return Math.max(...clips.map(c => c.start + c.duration));
  }

  /**
   * Initialize video and audio encoders
   */
  private async initializeEncoders(): Promise<void> {
    const resolution = ASPECT_RATIOS[this.config.aspectRatio];

    // Check WebCodecs support
    if (!('VideoEncoder' in window)) {
      throw new Error('WebCodecs API non supportée par ce navigateur');
    }

    // Try H.264
    const h264Configs = [
      'avc1.640028', // High Profile Level 4.0
      'avc1.4D0028', // Main Profile Level 4.0
      'avc1.42E01E', // Baseline Profile Level 3.0
    ];

    let selectedVideoCodec = '';

    for (const codec of h264Configs) {
      const config: VideoEncoderConfig = {
        codec,
        width: resolution.width,
        height: resolution.height,
        bitrate: this.config.videoBitrate,
        framerate: this.config.fps,
        latencyMode: 'quality',
      };
      try {
        const support = await VideoEncoder.isConfigSupported(config);
        if (support.supported) {
          selectedVideoCodec = codec;
          break;
        }
      } catch (e) {
        // Continue
      }
    }

    if (!selectedVideoCodec) {
      throw new Error('Configuration H.264 non supportée par le navigateur');
    }

    // Initialize MP4 Muxer
    this.muxer = new Muxer({
      target: new ArrayBufferTarget(),
      video: {
        codec: 'avc',
        width: resolution.width,
        height: resolution.height,
      },
      audio: {
        codec: 'aac',
        numberOfChannels: 2,
        sampleRate: this.config.audioSampleRate,
      },
      fastStart: 'in-memory',
    });

    // Initialize H.264 video encoder
    const videoConfig: VideoEncoderConfig = {
      codec: selectedVideoCodec,
      width: resolution.width,
      height: resolution.height,
      bitrate: this.config.videoBitrate,
      framerate: this.config.fps,
      latencyMode: 'quality',
    };

    this.videoEncoder = new VideoEncoder({
      output: (chunk, metadata) => {
        if (this.muxer) {
          this.muxer.addVideoChunk(chunk, metadata);
        }
      },
      error: (error) => {
        console.error('Video encoder error:', error);
        throw error;
      }
    });

    this.videoEncoder.configure(videoConfig);

    // Initialize AAC audio encoder
    // FIX: Cannot find name 'AudioEncoderConfig'. Use `any` as a workaround for missing WebCodecs types.
    const audioConfig: any = {
      codec: 'mp4a.40.2',
      sampleRate: this.config.audioSampleRate,
      numberOfChannels: 2,
      bitrate: this.config.audioBitrate,
    };

    // FIX: Cannot find name 'AudioEncoder'. Access from `window` as a workaround for missing WebCodecs types.
    const audioSupport = await (window as any).AudioEncoder.isConfigSupported(audioConfig);
    if (!audioSupport.supported) {
      throw new Error('Configuration AAC non supportée');
    }

    // FIX: Cannot find name 'AudioEncoder'. Access constructor from `window` as a workaround for missing WebCodecs types.
    this.audioEncoder = new (window as any).AudioEncoder({
      output: (chunk: any, metadata: any) => {
        if (this.muxer) {
          this.muxer.addAudioChunk(chunk, metadata);
        }
      },
      error: (error: any) => {
        console.error('Audio encoder error:', error);
        throw error;
      }
    });

    this.audioEncoder.configure(audioConfig);
  }

  /**
   * Encode video and audio streams
   */
  private async encodeVideoAndAudio(
    visibleClips: Clip[],
    audioClips: Clip[],
    duration: number,
    totalFrames: number,
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<void> {
    this.updateProgress({
      phase: 'encoding',
      progress: 20,
      message: 'Encodage des frames vidéo...',
      currentFrame: 0,
      totalFrames
    });

    const resolution = ASPECT_RATIOS[this.config.aspectRatio];
    const frameDuration = 1000000 / this.config.fps; // in microseconds

    // Create offscreen canvas for rendering with alpha support
    const canvas = new OffscreenCanvas(resolution.width, resolution.height);
    const ctx = canvas.getContext('2d', {
      alpha: true, // Enable alpha channel for transparency
      desynchronized: true
    });

    if (!ctx) {
      throw new Error('Impossible de créer le contexte canvas');
    }

    // Load all media elements
    await this.preloadMedia(visibleClips, audioClips);

    // Render and encode frames
    for (let frame = 0; frame < totalFrames; frame++) {
      if (this.aborted) {
        throw new Error('Export annulé');
      }

      const currentTime = frame / this.config.fps;

      try {
        // Render frame
        await this.renderFrame(
          ctx,
          canvas,
          currentTime,
          visibleClips,
          tracks,
          activeSubtitleLanguage,
          subtitleStyle
        );

        // Encode video frame
        const videoFrame = new VideoFrame(canvas, {
          timestamp: frame * frameDuration,
          duration: frameDuration,
        });

        if (this.videoEncoder) {
          this.videoEncoder.encode(videoFrame, { keyFrame: frame % (this.config.fps * 2) === 0 });
          videoFrame.close();
        }
      } catch (error) {
        // Log frame rendering errors but continue export
        console.error(`Error rendering frame ${frame}:`, error);
        // Check if it's a critical memory error
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage.toLowerCase().includes('memory') ||
          errorMessage.toLowerCase().includes('out of') ||
          (error as any).name === 'QuotaExceededError') {
          throw new Error(`Erreur mémoire lors du rendu de la frame ${frame}. Vidéo source trop volumineuse.`);
        }
        // For non-critical errors, continue with next frame
      }

      // Update progress every 10 frames
      if (frame % 10 === 0) {
        const progress = 20 + Math.floor((frame / totalFrames) * 60);
        this.updateProgress({
          phase: 'encoding',
          progress,
          message: `Encodage frame ${frame}/${totalFrames}`,
          currentFrame: frame,
          totalFrames
        });
      }
    }

    // Flush video encoder
    if (this.videoEncoder) {
      await this.videoEncoder.flush();
    }

    this.updateProgress({
      phase: 'encoding',
      progress: 85,
      message: 'Encodage audio...'
    });

    // Encode audio
    await this.encodeAudio(audioClips, duration);

    // Flush audio encoder
    if (this.audioEncoder) {
      await this.audioEncoder.flush();
    }

    this.updateProgress({
      phase: 'encoding',
      progress: 90,
      message: 'Encodage terminé'
    });
  }

  /**
   * Preload all media (videos, images, audio)
   */
  private async preloadMedia(visibleClips: Clip[], audioClips: Clip[]): Promise<void> {
    const resolution = ASPECT_RATIOS[this.config.aspectRatio];

    // Initialize frame renderer
    this.frameRenderer = new FrameRenderer(resolution.width, resolution.height);
    await this.frameRenderer.preloadMedia(visibleClips);

    // Initialize audio mixer
    this.audioMixer = new AudioMixer(this.config.audioSampleRate);
    await this.audioMixer.preloadAudio(audioClips);
  }

  private async renderFrame(
    ctx: OffscreenCanvasRenderingContext2D,
    canvas: OffscreenCanvas,
    currentTime: number,
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<void> {
    if (!this.frameRenderer) {
      throw new Error('Frame renderer not initialized');
    }

    // Ne pas passer activeSubtitleLanguage pour éviter de burn les sous-titres, SAUF si demandé explicitement
    const languageToUse = this.config.burnSubtitles ? activeSubtitleLanguage : '';

    await this.frameRenderer.renderFrame(
      ctx,
      currentTime,
      clips,
      tracks,
      languageToUse,
      subtitleStyle,
      this.config.scaleMode || 'fill'
    );
  }

  /**
   * Encode audio stream
   */
  private async encodeAudio(audioClips: Clip[], duration: number): Promise<void> {
    if (!this.audioMixer || !this.audioEncoder) {
      return; // No audio to encode
    }

    // Generate and encode audio frames
    const audioFrameGenerator = this.audioMixer.generateAudioFrames(audioClips, duration);

    let frameCount = 0;
    for await (const audioData of audioFrameGenerator) {
      if (this.aborted) {
        audioData.close();
        throw new Error('Export annulé');
      }

      this.audioEncoder.encode(audioData);
      audioData.close();

      frameCount++;

      // Update progress every 50 frames
      if (frameCount % 50 === 0) {
        this.updateProgress({
          phase: 'encoding',
          progress: 85 + Math.min(frameCount / 1000, 5), // Small incremental progress
          message: 'Encodage audio...'
        });
      }
    }
  }

  /**
   * Finalize and create MP4 blob
   */
  private async finalize(): Promise<Blob> {
    if (!this.muxer) {
      throw new Error('Muxer not initialized');
    }

    this.muxer.finalize();
    const { buffer } = this.muxer.target as ArrayBufferTarget;
    return new Blob([buffer], { type: 'video/mp4' });
  }

  /**
   * Cleanup resources
   */
  private cleanup() {
    if (this.videoEncoder?.state !== 'closed') {
      this.videoEncoder?.close();
    }
    if (this.audioEncoder?.state !== 'closed') {
      this.audioEncoder?.close();
    }
    if (this.frameRenderer) {
      this.frameRenderer.cleanup();
    }
    if (this.audioMixer) {
      this.audioMixer.cleanup();
    }
    this.muxer = undefined;
    this.frameRenderer = undefined;
    this.audioMixer = undefined;
  }

  /**
   * Update progress callback
   */
  private updateProgress(progress: ExportProgress) {
    this.progressCallback(progress);
  }
}

/**
 * Default export configuration
 */
export const DEFAULT_EXPORT_CONFIG: ExportConfig = {
  aspectRatio: '16:9',
  fps: 30,
  videoBitrate: 10_000_000, // 10 Mbps
  audioBitrate: 192_000,   // 192 kbps
  audioSampleRate: 48000,
  format: 'mp4',
};
